import React from 'react';
import * as ReactDOM from 'react-dom';
import AppStore from './container/app/appStore';
import { Provider } from 'mobx-react';
import { routingStore } from './routes';
import { configure } from 'mobx';
import Amplify, { Auth, Hub } from 'aws-amplify';
import appConfig from 'api/appConfig';
import { prewarmWafToken } from 'api/wafToken';
import { AppSyncClient } from './api/auth/appSyncClient';
import { populateUserGroups } from 'api/graphql/mutation';
import LoadingSpinner from 'components/loadingSpinner/loadingSpinner';
import App from 'app';
// import '@amzn/awsui-global-styles/polaris.css';
import '@amzn/arte-frontend/dist/arte.css';
import '@amzn/fintech-ui/styles';

// Built-in internationalization
// Ref: https://refresh.cloudscape.aws.dev/get-started/dev-guides/internationalization/
import { I18nProvider } from '@amzn/awsui-components-react/polaris/i18n';
import messages from '@amzn/awsui-components-react/polaris/i18n/messages/all.all';
import AppTopNav from 'container/app/topNav/appTopNav';
import { recoverFromChunkError } from 'utils/chunkLoadRecovery';

Amplify.configure(appConfig.AmplifyConfig);
// Warm the WAF token cache at startup so the first AppSync call doesn't block on a cold mint.
prewarmWafToken();
configure({ enforceActions: 'observed' });

// Suppress the benign "ResizeObserver loop completed with undelivered
// notifications" console error. This only silences the noisy error event; it
// does NOT patch window.ResizeObserver, so callback timing is unchanged for all
// existing (e.g. Cloudscape) consumers.
//
// Also recover from webpack lazy-chunk load failures as a backstop. Most lazy
// imports are behind the router's <ErrorBoundary>, which handles chunk errors
// itself (see errorBoundary.tsx). But some lazy imports (top nav, app shell)
// live outside that boundary; a chunk failure there surfaces as a window error
// or unhandled rejection, so we catch both here. recoverFromChunkError is
// window-capped (see chunkLoadRecovery) so it triggers a bounded number of
// reloads, preventing a loop if a chunk is genuinely gone.
window.addEventListener('error', (e) => {
  if (e.message && e.message.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return;
  }
  if (recoverFromChunkError(e.error || e.message)) {
    // Suppress the event before the reload takes over, so downstream handlers
    // (RUM, Sentry) don't report an error we're about to auto-recover from.
    // stopImmediatePropagation() stops later window listeners (this handler is
    // registered at bootstrap, before RUM init in routes()); preventDefault()
    // stops the browser's default console logging.
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// A failed dynamic import surfaces as an unhandled promise rejection, not always
// a window 'error' event -- handle both so chunk failures are always caught.
window.addEventListener('unhandledrejection', (e) => {
  if (recoverFromChunkError(e && e.reason)) {
    // Suppress the rejection before the reload takes over, so downstream handlers
    // (RUM, Sentry) don't report it. See the 'error' handler above.
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

Auth.currentSession()
  .then(async session => {
    const appsync = new AppSyncClient(session);
    const appStore = new AppStore(appsync, routingStore, session);
    try {
      await appStore.api.mutate(populateUserGroups, {});
    } catch {
      // do nothing here - If user group retrival fails, do no refresh the apge again
    }

    // Load agentic feature-flag rules + the user's groups. Runs after
    // populateUserGroups so group membership is available. Fails closed
    // internally, so a failure here leaves all gated flows disabled.
    try {
      await appStore.featureFlagStore.init(appStore.currentUserID);
    } catch {
      // ignore — store already fails closed
    }

    // Prevent redirecting to sign in on render issues
    try {
      ReactDOM.render(
        <Provider appStore={appStore}>
          <I18nProvider messages={[messages]}>
            <App />
          </I18nProvider>
        </Provider>, document.getElementById('root'),
      );
    } catch (error) {
      console.error('Error rendering React application:', error); // eslint-disable-line no-console
    }
  })
  .catch(() => {
    // Only sign-in if not already signed in.
    Auth.federatedSignIn({ customProvider: 'AmazonFederate', customState: window.location.href });
  });

Hub.listen('auth', ({ payload: { event, data } }) => {
  switch (event) {
    case 'customOAuthState':
      window.location.replace(data);
      break;
  }
});

const appStore = {} as AppStore;
const skeleton =
  <div className="awsui">
    <Provider appStore={appStore}>
      <AppTopNav isPlaceholder />
      <LoadingSpinner size="large" absoluteCenter />
    </Provider>
  </div>;

ReactDOM.render(
  skeleton, document.getElementById('root'),
);
